import React, { useEffect, useId, useMemo, useState } from "react";
import { CloseCircle, Message, Trash, Warning2 } from "iconsax-reactjs";
import {
  subscribeReelComments,
  deleteReelComment,
  threadComments,
  formatReelDateTime,
} from "../../utils/reelsShared";
import "./reels.css";

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function CommentRow({ comment, isReply, onDelete, busy }) {
  return (
    <li className={`reel-comment ${isReply ? "is-reply" : ""}`}>
      <span className="avatar reel-comment-avatar" aria-hidden="true">
        {comment.userAvatar ? <img src={comment.userAvatar} alt="" /> : initials(comment.userName)}
      </span>
      <div className="reel-comment-body">
        <div className="reel-comment-head">
          <span className="reel-comment-name">{comment.userName || "Unknown user"}</span>
          <span className="reel-comment-time">{formatReelDateTime(comment.createdAt)}</span>
        </div>
        <p className="reel-comment-text">{comment.text}</p>
      </div>
      <button
        type="button"
        className="btn-icon reel-action-danger"
        onClick={() => onDelete(comment)}
        disabled={busy}
        title="Remove this comment"
        aria-label={`Remove comment by ${comment.userName || "unknown user"}`}
      >
        <Trash size={16} variant="Linear" />
      </button>
    </li>
  );
}

/**
 * Comment moderation for one reel. Both roles that can reach this modal, an
 * admin or the dealer who owns the reel, may remove any comment on it, so
 * there is no per-row permission check here; the rules are the backstop.
 */
export default function ReelCommentsModal({ reel, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const titleId = useId();

  const reelId = reel?.id;

  useEffect(() => {
    if (!reelId) return undefined;
    setLoading(true);
    setError(null);
    const unsubscribe = subscribeReelComments(
      reelId,
      (list) => { setComments(list); setLoading(false); },
      () => { setError("Could not load comments."); setLoading(false); }
    );
    return () => unsubscribe();
  }, [reelId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const threads = useMemo(() => threadComments(comments), [comments]);

  const handleDelete = async (comment, replies = []) => {
    const message = replies.length > 0
      ? `Remove this comment and its ${replies.length} ${replies.length === 1 ? "reply" : "replies"}?`
      : "Remove this comment?";
    if (!window.confirm(message)) return;

    setBusyId(comment.id);
    try {
      await deleteReelComment(reelId, comment.id, replies.map((r) => r.id));
    } catch (err) {
      console.error("Failed to delete comment:", err);
      alert("Could not remove that comment. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  if (!reel) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal reel-comments-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-header">
          <div className="reel-modal-heading">
            <h2 id={titleId}>Comments</h2>
            <p className="reel-modal-sub">{reel.caption || "Property reel"}</p>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close comments">
            <CloseCircle size={20} variant="Linear" />
          </button>
        </div>

        <div className="reel-comments-body" aria-busy={loading}>
          {loading ? (
            <div aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="reel-comment-skeleton">
                  <div className="skeleton" />
                  <div className="reel-comment-skeleton-lines">
                    <div className="skeleton reel-skel-line is-short" />
                    <div className="skeleton reel-skel-line is-wide" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <p className="reel-error-banner" role="alert">
              <Warning2 size={18} variant="Linear" aria-hidden="true" />
              {error}
            </p>
          ) : threads.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon" aria-hidden="true">
                <Message size={22} variant="Linear" />
              </span>
              <h3>No comments yet</h3>
              <p>When people comment on this reel in the app, their comments appear here and you can remove any of them.</p>
            </div>
          ) : (
            <ul className="reel-comments-list">
              {threads.map(({ comment, replies }) => (
                <React.Fragment key={comment.id}>
                  <CommentRow
                    comment={comment}
                    onDelete={(c) => handleDelete(c, replies)}
                    busy={busyId === comment.id}
                  />
                  {replies.map((reply) => (
                    <CommentRow
                      key={reply.id}
                      comment={reply}
                      isReply
                      onDelete={(c) => handleDelete(c)}
                      busy={busyId === reply.id}
                    />
                  ))}
                </React.Fragment>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
